import React, { useState, useEffect, useRef, useMemo } from "react";
import TransferService from "service/TransferService";
import { useAuth } from "provider/AuthContext";
import SelectedDates from "../FilterFormComponents/DateFilter";
import SectionLoader from "components/ui/loaders/SectionLoader";
import { FETCH_DATE_AFTER_SELECTION, isEmpty } from "helper/Functions";
import RoleBasedDropdowns, { roleQueryParams } from "../FilterFormComponents/RoleBasedDropdown";
import { ReactComponent as IconSearch } from "assets/icons/icon-search.svg";
import { API_URL } from "helper/Constants";
import CustomTable from "components/ui/tables/CustomTable";

const TransferReport = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState("today");
  const [chosenDate, setChosenDate] = useState("");
  const [responseMsg, setReponseMsg] = useState("");
  const [date, setDate] = useState({ startDate: "", endDate: "" });
  const [userTransferReports, setUserTransferReports] = useState(null);
  const [transferReportLoading, setTransferReportLoading] = useState(false);

  const [selectedRole, setSelectedRole] = useState({});
  const [selectedInputValues, setSelectedInputValues] = useState({});

  const transferServ = new TransferService(API_URL);

  const handleReset = () => {
    setSelectedDate("today");
    setChosenDate("");
    setUserTransferReports(null);
  };

  // Calculate the difference dates
  useEffect(() => {
    const { startDate, endDate } = FETCH_DATE_AFTER_SELECTION(selectedDate, chosenDate);
    setDate({ ...date, startDate: startDate, endDate: endDate });
  }, [selectedDate, chosenDate]);

  const handleSearch = async () => {
    try {
      //console.log(selectedInputValues, "selectedInputValues");
      setTransferReportLoading(true);

      const entries = Object.entries(selectedInputValues);
      const lastSelectedUserId = entries.length > 0 ? entries[entries.length - 1][1] : null;

      const transferReportresponse = await transferServ.getAllTransferReports({
        startDate: date.startDate,
        endDate: date.endDate,
        userId: lastSelectedUserId ? lastSelectedUserId : user?._id,
      });
      // userId: selectedInputValues.value,
      if (transferReportresponse.success && transferReportresponse?.reports?.username) {
        setUserTransferReports(transferReportresponse?.reports);
      } else {
        console.error("Unexpected response structure:", transferReportresponse);
        setReponseMsg("No Reports found.");
      }
      setTransferReportLoading(false);
    } catch (error) {
      setTransferReportLoading(false);
      console.error("Error fetching users:", error);
      // Handle token expiration or invalid token (401)
      if (error.response?.status === 401) {
        setReponseMsg("Authorization failed. Please log in again.");
        // Optional: Redirect to login page
        // navigate("/login");
      } else {
        setReponseMsg("Error fetching users. Please try again later.");
      }
    } finally {
      setTransferReportLoading(false);
    }
  };

  const tableData = userTransferReports?.children?.map((item, index) => {
    return {
      ...item,
    };
  });

  const tableColumns = useMemo(() => [
    {
      accessorKey: "username",
      header: "Username",
      size: 200,
    },

    {
      accessorKey: "role",
      header: "Role",
      size: 50,
    },
    {
      accessorKey: "odds",
      header: "Money In",
      size: 200,
      Cell: ({ cell }) => Math.abs(cell.row.original.moneyIn).toFixed(2),
    },

    {
      accessorKey: "ticketCode",
      header: "Money Out",
      size: 200,
      Cell: ({ cell }) => Math.abs(cell.row.original.moneyOut).toFixed(2),
    },
    {
      accessorKey: "ticketCode",
      header: "+/-",
      size: 200,
      Cell: ({ cell }) => Math.abs(cell.row.original.difference).toFixed(2),
    },
  ]);

  return (
    <div className="flex flex-col w-full transfer-history">
      <header className="rounded-lg sm:text-start text-center bg-[#474747] text-white w-full p-3">
        <h1 className="md:text-2xl text-lg font-bold md:text-left text-center">Transfers Report</h1>
      </header>
      <div className="flex-1 pt-4">
        <div className="w-full rounded-lg">
          <SelectedDates title="Transaction Date" setSelectedDate={setSelectedDate} selectedDate={selectedDate} setCustomDate={setChosenDate} />
          <div className="flex flex-row gap-3 mt-4">
            <RoleBasedDropdowns
              baseRole={user?.role}
              selectedRole={selectedRole}
              setSelectedRole={setSelectedRole}
              selectedInputValues={selectedInputValues}
              setSelectedInputValues={setSelectedInputValues}
            />
          </div>

          <div className="lg:flex gap-3 mb-8 mt-7">
            <button
              className={`bg-primary hover:bg-primary/80 text-black w-full lg:max-w-36 font-bold py-2 rounded-lg `}
              type="button"
              onClick={handleSearch}
            // disabled={isEmpty(selectedInputValues)} // Disable button if no value is selected
            >
              SEARCH
            </button>
            <button
              className="border border-black hover:bg-gray-300 p-2 mt-2 sm:mt-0 text-gray-700 lg:max-w-40 font-bold w-full lg:py-2 px-11 rounded-lg"
              type="reset"
              onClick={handleReset}
            >
              RESET
            </button>
          </div>

          <hr className="border-t mt-5 mb-3 border-[#d4c8c8a3]" />

          {!transferReportLoading ? (
            userTransferReports?.username ? (
              <div className="overflow-x-auto">
                <table className="w-full table-auto border border-gray-300">
                  <thead className="bg-[#474747] font-medium text-sm text-white">
                    <tr>
                      <th className="py-2 px-4 border text-left">Username</th>
                      <th className="py-2 px-4 border text-left">Role</th>
                      <th className="py-2 px-4 border text-center">Money In</th>
                      <th className="py-2 px-4 border text-center">Money Out</th>
                      <th className="py-2 px-4 border text-center">+/-</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border px-4 text-black">{userTransferReports?.username}</td>
                      <td className="border px-4 text-black">{userTransferReports?.role}</td>
                      <td className="border text-center text-black">{userTransferReports?.moneyIn.toFixed(2)}</td>
                      <td className="border text-center text-black">{userTransferReports?.moneyOut.toFixed(2)}</td>
                      <td className="border text-center text-black">{userTransferReports?.difference.toFixed(2)}</td>
                    </tr>
                  </tbody>
                  {/* <tfoot className="bg-gray-800 font-bold">
                  <tr>
                    <td colSpan="2" className="border text-center">
                      Totals
                    </td>
                    <td className="border text-center">{totalMoneyIn.toFixed(2)}</td>
                    <td className="border text-center">{totalMoneyOut.toFixed(2)}</td>
                    <td className="border text-center">{totalDifference.toFixed(2)}</td>
                  </tr>
                </tfoot> */}
                </table>
                {userTransferReports?.children?.length ? (
                  <div className="mt-3">
                    <CustomTable
                      columns={tableColumns}
                      data={tableData ? tableData : []}
                      enableGlobalFilter={true}
                      enableFilterMatchHighlighting={true}
                    />
                  </div>
                ) : // <table className="w-full table-auto border border-gray-300 mt-5">
                  //   <thead className="bg-[#474747] font-medium text-sm">
                  //     <tr>
                  //       <th className="py-2 px-4 border text-left">Username</th>
                  //       <th className="py-2 px-4 border text-left">Role</th>
                  //       <th className="py-2 px-4 border text-center">Money In</th>
                  //       <th className="py-2 px-4 border text-center">Money Out</th>
                  //       <th className="py-2 px-4 border text-center">+/-</th>
                  //     </tr>
                  //   </thead>
                  //   <tbody>
                  //     {userTransferReports?.children?.map((child) => {
                  //       return (
                  //         <tr>
                  //           <td className="border px-4 text-black">{child?.username}</td>
                  //           <td className="border px-4 text-black">{child?.role}</td>
                  //           <td className="border text-center text-black">{child?.moneyIn.toFixed(2)}</td>
                  //           <td className="border text-center text-black">{child?.moneyOut.toFixed(2)}</td>
                  //           <td className="border text-center text-black">{child?.difference.toFixed(2)}</td>
                  //         </tr>
                  //       );
                  //     })}
                  //   </tbody>
                  // </table>
                  null}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 px-3 my-7">
                <div className="bg-[#EDEEF5] rounded-lg p-6 sm:p-8 [&_svg_path]:fill-slate-500">
                  <IconSearch />
                </div>
                <span className="text-lg sm:text-xl mt-8 mb-4 text-[#474747] text-center w-full max-w-[300px]">
                  {responseMsg ? responseMsg : "Search all available records by selecting one of the filters above!"}
                </span>
              </div>
            )
          ) : (
            <SectionLoader bgColor="light" />
          )}
        </div>
      </div>
    </div>
  );
};

export default TransferReport;
